import {
    buildDeliveryAvailability,
    dataLayerPush,
    getNavigationId,
    getNavigationName,
    getSearchStats,
    getSkuSearchTerm,
    getStoreId,
    processFindingMethod,
    resolveItemSearchTerm,
    setSkuFindingMethod,
    setSkuSearchTerm,
    validateStockStatus,
} from '@libs/analytics';
import {
    BRANCH_CONTENT_SCHEMA_COMMERCE,
    BRANCH_PRODUCT_CATEGORY,
    decodeFref,
    getENV,
    getQueryStringValue,
    NavigationListTypes,
    trackBranchAddToCart,
    useCart,
    useDecisions,
} from '@libs/common-next';
import {
    AUTOSHIP_ONE_TIME_OPTION,
    AUTOSHIP_ONE_TIME_SELECTION_VALUE,
    getAutoshipCadenceByValue,
} from '@libs/common-types';

import { isProductAvailableForAutoship, useProduct } from '@/entities/product';
import { type FormFields, usePurchaseFormValue } from '@/entities/purchase-form';
import type {
    AddToCartBaseEvent,
    AddToCartBaseItem,
    AutoshipEnrichmentData,
    FulfilmentMethodSelection,
} from '@/shared/analytics';
import { enrichAddToCartEvent } from '@/shared/analytics';
import { useAutoshipSubscription } from '@/widgets/product-details/subscribed-with-autoship';

const getAutoshipAnalyticsFrequencyLabel = (value: string): string => {
    if (value === AUTOSHIP_ONE_TIME_SELECTION_VALUE) {
        return AUTOSHIP_ONE_TIME_OPTION.label;
    }

    const cadence = getAutoshipCadenceByValue(value);

    if (!cadence) {
        return '';
    }

    // Analytics reports months in the singular, unlike the UI label.
    const analyticsUnit = cadence.frequencyUnit === 'months' ? 'month' : cadence.frequencyUnit;

    return `${cadence.frequency} ${analyticsUnit}`;
};

export const useAddToCartAnalytics = () => {
    const { product, base, tailored } = useProduct();
    const { itemsList } = useCart();

    const autoshipFlag = useDecisions('ff_autoship');
    const isSubscribedIndicatorEnabled = useDecisions('x_as_sub_ind');
    const isBranchTrackingEnabled = useDecisions('ff_brch_evts');
    const getValues = usePurchaseFormValue();
    const { isReady, isSubscribed } = useAutoshipSubscription();

    const isCA = getENV('NEXT_PUBLIC_LOCALE') === 'en-CA';
    const currency = isCA ? 'CAD' : 'USD';

    const sendAnalytics = (isProductRecommendation?: boolean) => {
        if (!product || !base || !tailored) return;

        const { categories, merchandisingHierarchy } = base;
        const { sku, webStyleId, name, brand, variantValues, rating, deliveryMethods } = product;

        const {
            quantity,
            fulfilmentMethod: selectedDeliveryMethod,
            purchaseOption: selectedPurchaseOption,
            autoshipFrequency: selectedAutoshipFrequency,
            autoshipUnit: selectedAutoshipUnit,
        } = getValues();

        const price = product.price.sale ?? product.price.original;
        const frefData = decodeFref();
        const trackingParam = processFindingMethod(window.sessionStorage.pdpFindingMethod);

        // If fref is present, store the raw fref string for cart/checkout attribution.
        // Otherwise fall back to the legacy sessionStorage finding method.
        const frefParam = getQueryStringValue('fref');
        setSkuFindingMethod(sku, frefParam ?? window.sessionStorage.pdpFindingMethod);

        const storeId = getStoreId({
            fulfilmentMethod: selectedDeliveryMethod,
            tailoredVariant: tailored.variants?.[sku],
        });
        const autoshipFrequencyKey = `${selectedAutoshipFrequency}_${selectedAutoshipUnit}`;
        const autoshipFrequencyOption = getAutoshipAnalyticsFrequencyLabel(autoshipFrequencyKey);

        const { searchTerm } = getSearchStats();
        const isCustomPharmacyOOS = Boolean(product?.pharmacy?.oos);

        const existingSubscription = isSubscribedIndicatorEnabled && isReady && isSubscribed ? 'yes' : 'no';

        const itemListId = frefData?.item_list_id ?? getNavigationId(trackingParam);
        const itemListName = frefData?.item_list_name ?? getNavigationName(trackingParam);
        const isFromSearch = itemListId === NavigationListTypes.SEARCH;

        // Preserve per-SKU term from view_item when SearchTermGuard cleared psLastSearchedTerm but fref still implies search.
        if (isFromSearch && searchTerm) {
            setSkuSearchTerm(sku, searchTerm);
        } else if (!isFromSearch) {
            setSkuSearchTerm(sku, null);
        }

        const resolvedItemSearchTerm = resolveItemSearchTerm(
            getSkuSearchTerm(sku),
            isFromSearch ? searchTerm || null : null
        );

        const baseItemData: AddToCartBaseItem = {
            autoship_autoselected: 'no',
            autoship_availability: isProductAvailableForAutoship(product) ? 'yes' : 'no',
            autoship_frequency: selectedPurchaseOption === 'autoship' ? autoshipFrequencyOption : null,
            color: variantValues?.color ?? '',
            default_version: sku === base.defaultSku ? 'yes' : 'no',
            existing_subscription: existingSubscription,
            flavor: variantValues?.flavor ?? '',
            fulfillment_method: `${selectedDeliveryMethod} ${selectedPurchaseOption}` as FulfilmentMethodSelection,
            item_brand: brand?.name ?? '',
            item_cart_order: itemsList.length + 1,
            item_id: webStyleId,
            ...(frefData && { index: frefData.index }),
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_name: name,
            item_search_term: resolvedItemSearchTerm,
            item_variant: sku,
            price,
            item_category: merchandisingHierarchy?.vicePresidentName ?? '',
            item_category2: merchandisingHierarchy?.division ?? '',
            item_category3: merchandisingHierarchy?.department ?? '',
            item_category4: merchandisingHierarchy?.class ?? '',
            item_category5: merchandisingHierarchy?.category ?? '',
            quantity,
            size: variantValues?.size ?? '',
            store_id: storeId,
        };

        const baseEvent: AddToCartBaseEvent = {
            event: 'add_to_cart',
            ecommerce: {
                add_to_cart_type: 'add',
                category: categories[0] ?? '',
                currency,
                pdp_type: 'standard pdp',
                product_recommendation: isProductRecommendation ? 'yes' : 'no',
                sub_category1: categories[1] ?? '',
                sub_category2: categories[2] ?? '',
                value: price * quantity,
                items: [baseItemData],
            },
        };

        const deliveryMethod = selectedDeliveryMethod.toUpperCase() as Uppercase<FormFields['fulfilmentMethod']>;

        const enrichmentData: AutoshipEnrichmentData =
            autoshipFlag === 'precrawl'
                ? {
                      autoshipFlag: 'precrawl',
                      stockAvailability: validateStockStatus({
                          isCustomPharmacyOOS,
                          availableStock: tailored.variants[sku]?.[deliveryMethod]?.availableStock ?? 0,
                          minimumCaseQty: deliveryMethods[deliveryMethod]?.purchaseQuantities?.min ?? 1,
                      }),
                  }
                : {
                      autoshipFlag: 'crawl',
                      deliveryAvailability: buildDeliveryAvailability({
                          deliveryMethods,
                          tailoredVariant: tailored.variants[sku],
                          purchaseOption: selectedPurchaseOption,
                          isCustomPharmacyOOS,
                      }),
                  };

        dataLayerPush(enrichAddToCartEvent(baseEvent, enrichmentData));

        if (isBranchTrackingEnabled) {
            trackBranchAddToCart(
                {
                    currency,
                    value: price * quantity,
                    product_recommendation: isProductRecommendation ? 'yes' : 'no',
                    description: 'Customer added item to cart',
                    category: categories[0] ?? '',
                },
                [
                    {
                        $content_schema: BRANCH_CONTENT_SCHEMA_COMMERCE,
                        $price: price,
                        $quantity: quantity,
                        $sku: sku,
                        $product_name: name,
                        $product_brand: brand?.name ?? '',
                        $product_category: BRANCH_PRODUCT_CATEGORY,
                        $product_variant: sku,
                        $rating_average: rating.average,
                        $rating_count: rating.count,
                    },
                ]
            );
        }
    };

    return { sendAnalytics };
};
